/**
 * PRODUCTION FIX: Input validation for node operations
 * Prevents circular references, invalid state transitions, and malformed operations
 */

import { NodeOperation, ProjectNode } from '@/shared/types/projectData';
import { validConditionalContentUpdate } from './conditionalNodeUpdate';
import {
  NODE_TYPE_CONVERSION_DESCENDANTS_ERROR,
  findProjectNodeById,
  getNodeTypeConversionPolicy,
  getSafeDocumentNormalizationPolicy,
  parentCanAcceptChildren,
} from '@/shared/utils/nodeTypeConversionPolicy';

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate node operation before execution
 */
export function validateNodeOperation(
  operation: NodeOperation,
  projectNodes?: ProjectNode[]
): ValidationResult {
  // Validate operation has required type
  if (!operation.type) {
    return { valid: false, error: 'Operation type is required' };
  }

  if (Object.prototype.hasOwnProperty.call(operation, 'expectedFields') &&
      (operation.type !== 'update' || operation.metadata?.persistence !== undefined ||
       !validConditionalContentUpdate(operation.data, operation.expectedFields))) {
    return { valid: false, error: 'Conditional updates require matching expectations for every content field.' };
  }

  if (operation.type === 'copy-nodes' || operation.type === 'cut-nodes') {
    if (!Array.isArray(operation.nodeIds) || operation.nodeIds.length === 0 ||
        operation.nodeIds.some(id => typeof id !== 'string' || !id.trim())) {
      return { valid: false, error: 'Clipboard operations require non-empty node IDs' };
    }
    if (projectNodes && operation.nodeIds.some(id => !findProjectNodeById(projectNodes, id))) {
      return { valid: false, error: 'Clipboard node was not found in this project' };
    }
    return { valid: true };
  }

  const columnOperationTypes = new Set([
    'add-column',
    'update-column',
    'delete-column',
    'reorder-columns',
  ]);
  const projectScopedOperationTypes = new Set([
    'initial-load',
  ]);

  // Validate nodeId format for operations that require it
  if (
    operation.type !== 'create' &&
    operation.type !== 'batch-update' &&
    operation.type !== 'batch-delete' &&
    !columnOperationTypes.has(operation.type) &&
    !projectScopedOperationTypes.has(operation.type)
  ) {
    if (!operation.nodeId) {
      return { valid: false, error: `nodeId required for ${operation.type} operation` };
    }

    // Validate nodeId format (should be string)
    if (typeof operation.nodeId !== 'string' || operation.nodeId.trim() === '') {
      return { valid: false, error: 'nodeId must be a non-empty string' };
    }
  }

  // Validate operation-specific requirements
  switch (operation.type) {
    case 'update':
      if (!operation.data || Object.keys(operation.data).length === 0) {
        return { valid: false, error: 'update operation requires non-empty data object' };
      }
      if (operation.data.parentId && (
        operation.nodeId === operation.data.parentId ||
        (projectNodes && checkForCircularReference(projectNodes, operation.nodeId!, operation.data.parentId))
      )) {
        return { valid: false, error: 'Update would create circular reference in tree structure' };
      }
      if (projectNodes && operation.data.parentId && !parentCanAcceptChildren(projectNodes, operation.data.parentId)) {
        return { valid: false, error: NODE_TYPE_CONVERSION_DESCENDANTS_ERROR };
      }
      if (projectNodes && operation.nodeId && operation.data.type) {
        const currentNode = findProjectNodeById(projectNodes, operation.nodeId);
        if (!currentNode) {
          return { valid: false, error: `Node ${operation.nodeId} not found for update operation` };
        }
        const conversionPolicy = operation.data.type === 'document'
          ? getSafeDocumentNormalizationPolicy(currentNode, operation.data.type, projectNodes)
          : getNodeTypeConversionPolicy(
              currentNode,
              operation.data.type,
              projectNodes,
              operation.data,
            );
        if (!conversionPolicy.allowed) {
          return { valid: false, error: conversionPolicy.reason };
        }
      }
      break;

    case 'move':
      // Check for self-parent assignment
      if (operation.nodeId === operation.targetParentId) {
        return { valid: false, error: 'Cannot move node to itself as parent (circular reference)' };
      }

      // Check for circular reference if project nodes are provided
      if (projectNodes && operation.targetParentId) {
        if (!parentCanAcceptChildren(projectNodes, operation.targetParentId)) {
          return { valid: false, error: NODE_TYPE_CONVERSION_DESCENDANTS_ERROR };
        }
        const wouldCreateCycle = checkForCircularReference(
          projectNodes,
          operation.nodeId!,
          operation.targetParentId
        );

        if (wouldCreateCycle) {
          return { valid: false, error: 'Move would create circular reference in tree structure' };
        }
      }
      break;

    case 'delete':
      if (!operation.nodeId) {
        return { valid: false, error: 'delete operation requires nodeId' };
      }
      break;

    case 'restore-tree':
      if (!operation.sourceOperationId) return {valid:false,error:'Subtree restore requires a deletion receipt'};
      break;

    case 'batch-update': {
      if (!operation.updates || !Array.isArray(operation.updates) || operation.updates.length === 0) {
        return { valid: false, error: 'batch-update operation requires non-empty updates array' };
      }

      const pendingNodes = projectNodes?.map(node => ({ ...node }));
      for (const update of operation.updates) {
        if (!update.nodeId) {
          return { valid: false, error: 'Each batch update must have a nodeId' };
        }
        if (!update.data || Object.keys(update.data).length === 0) {
          return { valid: false, error: 'Each batch update must have non-empty data' };
        }
        const result = validateNodeOperation({ type: 'update', nodeId: update.nodeId, data: update.data }, pendingNodes);
        if (!result.valid) return result;
        if (pendingNodes) {
          const currentNode = findProjectNodeById(pendingNodes, update.nodeId);
          if (currentNode) Object.assign(currentNode, update.data);
        }
      }
      break;
    }

    case 'batch-delete':
      if (!operation.nodeIds || !Array.isArray(operation.nodeIds) || operation.nodeIds.length === 0) {
        return { valid: false, error: 'batch-delete operation requires non-empty nodeIds array' };
      }
      break;

    case 'create':
      if (!operation.data) {
        return { valid: false, error: 'create operation requires data object' };
      }
      if (!operation.data.type) {
        return { valid: false, error: 'create operation data must have a type (task, group, milestone, document)' };
      }
      if (!['task', 'group', 'milestone', 'document'].includes(operation.data.type)) {
        return { valid: false, error: 'create operation data type must be task, group, milestone, or document' };
      }
      if (projectNodes) {
        const requestedParentId = operation.parentId ?? operation.data.parentId;
        if (requestedParentId && !parentCanAcceptChildren(projectNodes, requestedParentId)) {
          return { valid: false, error: NODE_TYPE_CONVERSION_DESCENDANTS_ERROR };
        }
      }
      break;

    case 'add-column':
      if (!operation.columnData) {
        return { valid: false, error: 'add-column operation requires columnData' };
      }
      if (!operation.columnId && !operation.columnData.id) {
        return { valid: false, error: 'add-column operation requires columnId' };
      }
      break;

    case 'update-column':
      if (!operation.columnId) {
        return { valid: false, error: 'update-column operation requires columnId' };
      }
      if (!operation.columnData || Object.keys(operation.columnData).length === 0) {
        return { valid: false, error: 'update-column operation requires non-empty columnData' };
      }
      break;

    case 'delete-column':
      if (!operation.columnId) {
        return { valid: false, error: 'delete-column operation requires columnId' };
      }
      break;

    case 'reorder-columns':
      if (!operation.columnOrder || !Array.isArray(operation.columnOrder) || operation.columnOrder.length === 0) {
        return { valid: false, error: 'reorder-columns operation requires non-empty columnOrder array' };
      }
      break;
  }

  return { valid: true };
}

/**
 * Check if moving nodeId to targetParentId would create a circular reference
 */
function checkForCircularReference(
  nodes: ProjectNode[],
  nodeId: string,
  targetParentId: string
): boolean {
  // If target parent is the node itself, that's circular
  if (nodeId === targetParentId) {
    return true;
  }

  // Walk up the tree from targetParent to see if we encounter nodeId
  const resolvedNodeId = findProjectNodeById(nodes, nodeId)?.id || nodeId;
  const visited = new Set<string>();
  let currentId: string | null | undefined = targetParentId;

  while (currentId) {
    const currentNode: Partial<ProjectNode> | undefined = findProjectNodeById(nodes, currentId);
    const resolvedCurrentId = currentNode?.id || currentId;
    if (visited.has(resolvedCurrentId)) return true;
    visited.add(resolvedCurrentId);

    // If we reach the node being moved, it's circular
    if (resolvedCurrentId === resolvedNodeId) {
      return true;
    }

    // Find the parent of current node
    if (!currentNode) {
      break; // Node not found, can't continue
    }

    currentId = currentNode.parentId;
  }

  return false; // No circular reference detected
}
